import { useState, useEffect, useMemo } from 'react';
import {
    Paper, Box, IconButton, Tooltip, Autocomplete, ListItem, ListItemText,
    TextField, Divider, Button, Card, Typography
} from '@mui/material';
import { ActionButton, TemplatePopup, renderTitle } from "./SarasCommonComp";
import RestAPIService from '../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../Utility/API/Serviceurls';
import DeleteIcon from '@mui/icons-material/Delete';
import { DeletePopup } from '../../../../Components/Common Components/CommonComponents';
import { Colors } from '../../../../Styles/Colors';
import ShowComponents from '../Common/ShowComponent';
import SarasToolsFieldWithMic from './SarasToolsFieldWithMic';
import { debounce } from '../../../../Components/CommonFunctions/CommonFunctions';
import PreviousDiagnosis from '../Common/PreviousDiagnosis';
import HistoryIcon from '@mui/icons-material/History';


const debouncedApiCall = debounce((apiCall) => apiCall(), 500);
const stripHtml = (html) => html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();

const SarasBoldItalic = ({ Title, ...props }) => {
    const urlConfig = useMemo(() => {
        return props.fromIp
            ? {
                "symptoms": { url: "SAR_IP_DOC_NOTES_SYMPTOMNS", postKey: "description" },
                "Complaints": { url: "SAR_IP_DOC_NOTES_COMPLAINTS", postKey: "description" },
                "final_diagnosis": { url: "SAR_IP_DOC_NOTES_FINALDIAGNOSIS", postKey: "description" },
                "plan": { url: "SAR_IP_DOC_NOTES_PLAN", postKey: "description" },
                "general_advice": { url: "SAR_IP_DOC_NOTES_GENERALADVICE", postKey: "description" },
                "provisional_diagnosis": { url: "SAR_IP_DOC_NOTES_PRODIAGNOSIS", postKey: "description" },
                "new_layout": { url: "SAR_IP_DOC_NOTES_NEW_LAYOUT_DATA", postKey: "comments" },
            } : {
                "Complaints": { url: "SARAS_DOC_COMPLAINTS", postKey: "complaints" },
                "symptoms": { url: "SARAS_DOC_SYMPTOMS", postKey: "symptoms" },
                "allergies": { url: "SARAS_DOC_ALLERGIES", postKey: "medication_allergy", postKey2: "non_medication_allergy" },
                "final_diagnosis": { url: "SARAS_DOC_FNLDIAGNOSIS", postKey: "final_diagnosis" },
                "plan": { url: "SARAS_DOC_PLAN", postKey: "plans" },
                "diagnosis": { url: "SARAS_DOC_PROVDIAGNOSIS", postKey: "provisional_diagnosis" },
                "general_advice": { url: "SARAS_DOC_GENERALADVICE", postKey: "general_advice" },
                "new_layout": { url: "SARAS_DOC_NEW_LAYOUT", postKey: "comments" },
            };
    }, [props.field_name]);

    const url_history = useMemo(() => {
        return {
            "final_diagnosis": {url: "SARAS_DOC_PREVIOUS_FINAL_DIAGNOSIS_GET", getKey: "final_diagnosis" },
            "diagnosis": {url: "SARAS_DOC_PREVIOUS_PROVISIONAL_GET", getKey: "provisional_diagnosis" }
        }
    }, [props.field_name])

    const showTitle = props?.field_name === "diagnosis" ? "Provisional Diagnosis" : Title;
    const fieldName = props?.isNewLayout ? "new_layout" : "";
    const [state, setState] = useState({
        post_key: "",
        post_key2: "",
        url: "",
        comments: "",
        comments2: "",
        // template state
        templateList: [],
        selectedTemplateName: null,
        openPopupAddNew: false,
        templateName: '',
        deleteTemplateName: {},
        tempType: '',
        deletePopup: false,
        templateLoad: false,
        removeData: false,
        //history state
        PreviousDiagnosisPopup: false,
        dateList: [],
        diagnosisData: [],
        fnlDateList: [],
        fnlDiagnosisData: [],
        is_previous_loading: false,
    });
    const [focusedEditor, setFocusedEditor] = useState(null);
    const [childGetFunc, setChildGetFunc] = useState(null);
    const [loader, setLoader] = useState(false);
    const [clear, setClear] = useState(false);

    useEffect(() => {
        if (props.isNewLayout) {
            updateState({ comments: props.stateValue || "" })
        }
    }, [props.isNewLayout, props.stateValue])

    useEffect(() => {
        const handleKeyDown = (e) => {
            try {
                if (props?.isSelected && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'g') {
                    e.preventDefault();
                    updateState({ openPopupAddNew: true, templateLoad: false, tempType: 'Add' });
                }
            } catch (err) {
                console.error('KeyDown failed:', err);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [props?.isSelected]);

    // Keyboard shortcut Ctrl + Shift + H
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (props?.isSelected && event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "h") {
                event.preventDefault();
                postSuggest();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [focusedEditor, props?.isSelected]);

    const getApiCall = () => {
        if (props.isNewLayout) {
            debouncedApiCall(props.GetAPiCall)
        } else {
            getToolsFieldData();
            if (props.fromIp === false && (props.field_name === "final_diagnosis" || props.field_name === "diagnosis")) {
                getPreviousDiagnosis();
            }
        }
        getTemplateData();
    }

    // api call of the component start
    const getToolsFieldData = () => {
        const { url, postKey, postKey2 } = urlConfig[props?.field_name] || {};
        if (!url) return;
        try {
            updateState({ post_key: postKey, url: url, post_key2: postKey2 || "" });
            let params = props?.fromIp ?
                `${Serviceurls[url]}?clinic_id=${props?.clinicId}&ip_admission_id=${props.admission_id}&doctor_id=${props?.doctor_id}`
                : `${Serviceurls[url]}?appointment_id=${props.appointmentId}`
            RestAPIService.getAll(params)
                .then((response) => {
                    if (response.data.status === 'success') {
                        const Data = response.data.data;
                        updateState({
                            comments: response.data.data[postKey] || "",
                            comments2: Data[postKey2] || "",
                        })
                        if (props.field_name === "allergies") {
                            props.setAllergyData(response.data.data);
                        }
                    }
                }).catch((error) => {
                    props.errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            props.errorMessage(error.message)
        }
    }

    const getPreviousDiagnosis = (date = "") => {
        try {
            const { url, getKey } = url_history[props?.field_name] || {};
            if (!url) return;
            const { appointmentId, patientId } = props
            const urls = `${Serviceurls[url]}?patient_id=${patientId}&appointment_id=${appointmentId}`
            updateState({
                [props?.field_name === "final_diagnosis" ? "fnlDiagnosisData" : "diagnosisData"]: [],
                is_previous_loading: true
            });
            RestAPIService.getAll(urls + (date ? `&date=${date}` : ""))
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        const responseData = response?.data?.data || [];
                        const isFinal = props?.field_name === "final_diagnosis";
                        const dates = date ? state[isFinal ? "fnlDateList" : "dateList"] : response?.data?.dates || [];

                        updateState({
                            [isFinal ? "fnlDateList" : "dateList"]: dates || state[isFinal ? "fnlDateList" : "dateList"],
                            [isFinal ? "fnlDiagnosisData" : "diagnosisData"]: responseData?.map((item) => {
                                return ({
                                    appointment_id: item.appointment_id,
                                    diagnosis: item?.diagnosis?.[0]?.[getKey] || item?.diagnoses?.[0]?.[getKey] || ""
                                })
                            }) || [],
                            is_previous_loading: false
                        });
                    }
                }).catch((error) => {
                    updateState({is_previous_loading: false})
                    props.errorMessage(error?.response?.data?.message || error?.message)
                })
        } catch (error) {
            updateState({is_previous_loading: false})
            props.errorMessage(error.message)
        }
    }

    const postToolsFieldData = (stateKey, value) => {
        try {
            const { url, postKey, postKey2 } = props?.isNewLayout ? urlConfig[fieldName] || {} : urlConfig[props?.field_name] || {};
            if (!url) return;
            if (stripHtml(value)?.length === 0) {
                props.errorMessage(`Please enter ${showTitle}`)
                setLoader(false);
                return;
            } else {
                const data = {
                    "appointment_id": props.appointmentId,
                    "patient_id": props.patientId,
                    // [postKey]: state.comments || ""
                    [postKey]: stateKey === "comments" ? value || "" : state.comments || "",
                }
                if (props.fromIp) {
                    data["ip_admission_id"] = props?.admission_id ?? null
                    data['clinic_id'] = +props?.clinicId
                    data['doctor_id'] = props?.doctor_id
                    if(props?.field_name === 'discharge_notes' || props?.field_name === 'family_counselling' || props?.field_name === 'medication_instruction'){
                        data['change_data'] = postKey
                        data['notes'] = stateKey === "comments" ? value || "" : state.comments || ""
                        delete data[postKey]
                        delete data['appointment_id']
                        delete data['patient_id']
                    }
                }
                if (props.field_name === "allergies" && stateKey === "comments2") {
                    data[postKey2] = value || "";
                    // data[postKey2] = state.comments2 || "";
                }
                if (props.isNewLayout) {
                    data["system_name"] = props.systemName || "";
                    data["custom_name"] = Title;
                }
                RestAPIService.create(data, Serviceurls[url])
                    .then((response) => {
                        if (response?.data?.status === 'success') {
                            props.successMessage(response.data.message)
                            if (props.isNewLayout) {
                                debouncedApiCall(props.GetAPiCall)
                            } else {
                                getToolsFieldData();
                            }
                            updateState({ selectedTemplateName: null, PreviousDiagnosisPopup: false });
                            setLoader(false);
                            if(props?.fromIp){
                                props?.callHistory(true)
                            }
                        }
                    }).catch((error) => {
                        setLoader(false);
                        props.errorMessage(error?.response?.data?.message || error.message)
                    })
            }
        } catch (error) {
            setLoader(false);
            props.errorMessage(error.message)
        }
    }

    // api call of the component end

    // api call of templateData start
    const getTemplateData = () => {
        const { url } = props?.isNewLayout ? urlConfig[fieldName] || {} : urlConfig[props?.field_name] || {};
        if (!url) return;
        let apiUrl = Serviceurls[url + "_TEMP_GET"];
        if (props?.isNewLayout && !props?.fromIp) {
            apiUrl += `?system_name=${props.systemName}`
        }else if(props?.isNewLayout && props?.fromIp){
            apiUrl += `?system_name=${props.systemName}&clinic_id=${props?.clinicId}&ip_admission_id=${props?.admission_id}&doctor_id=${props?.doctor_id}`
        } else if(props?.fromIp){
            apiUrl += `?clinic_id=${props?.clinicId}&ip_admission_id=${props?.admission_id}&doctor_id=${props?.doctor_id}`
        }
        try {
            RestAPIService.getAll(apiUrl)
                .then((response) => {
                    if (response.data.status === 'success') {
                        updateState({ templateList: response.data.data })
                    }
                }).catch((error) => {
                    props.errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            props.errorMessage(error.message)
        }
    }

    const postNewTemplateData = () => {
        try {
            const { url, postKey, postKey2 } = props?.isNewLayout ? urlConfig[fieldName] || {} : urlConfig[props?.field_name] || {};
            if (!url) return;
            updateState({ templateLoad: true })
            const data = {
                "id": state.tempType === 'rename' ? state.selectedTemplateName?.id : null,
                "template_name": state.templateName,
                [postKey]: state.comments
            }
            if (props?.fromIp) {
                data['clinic_id'] = props?.clinicId || null
                data['ip_admission_id'] = props?.admission_id || null
                data['doctor_id'] = props?.doctor_id || null
                data['patient_id'] = props?.patientId || null
            }
            if (props.field_name === "allergies") {
                data[postKey2] = state.comments2;
            }
            if (props.isNewLayout) {
                data["system_name"] = props.systemName || "";
                data["custom_name"] = Title;
            }
            if (!state.templateName) {
                props.errorMessage("Please enter template name")
                updateState({ templateLoad: false })
            } else if (!state.comments) {
                props.errorMessage(`Please enter ${showTitle}`)
            } else if (props.field_name === "allergies" && !state.comments2) {
                props.errorMessage(`Please enter ${showTitle}`)
            } else {
                RestAPIService.create(data, Serviceurls[url + "_TEMP_GET"])
                    .then((response) => {
                        if (response?.data?.status === 'success') {
                            props.successMessage(response.data.message)
                            updateState({
                                templateLoad: false,
                                openPopupAddNew: false,
                                templateName: '',
                                selectedTemplateName: '',
                                isEdit: false
                            })
                            getTemplateData()
                        }
                    }).catch((error) => {
                        updateState({ templateLoad: false })
                        props.errorMessage(error?.response?.data?.message || error.message)
                    })
            }
        } catch (error) {
            updateState({ templateLoad: false })
            props.errorMessage(error.message)
        }
    }

    const deleteTemplateData = () => {
        try {
            const { url } = props?.isNewLayout ? urlConfig[fieldName] || {} : urlConfig[props?.field_name] || {};
            if (!url) return;
            updateState({ removeData: true });
            let apiUrl = `${Serviceurls[url + "_TEMP_GET"]}?id=${state.deleteTemplateName?.id}`
            if (props?.isNewLayout) {
                apiUrl += `&system_name=${props.systemName}`
            }
            RestAPIService.delete(apiUrl)
                .then(response => {
                    if (response.data.status === "success") {
                        props.successMessage(response.data.message);
                        updateState({
                            openPopupAddNew: false,
                            templateLoad: false,
                            deleteTemplateName: {},
                            selectedTemplateName: '',
                            deletePopup: false,
                            removeData: false,
                            isEdit: false
                        });
                        getTemplateData();
                    }
                })
                .catch(e => {
                    updateState({ removeData: false });
                    props.errorMessage(e.response.data.message || e.message)
                });
        } catch (e) {
            updateState({ removeData: false });
            props.errorMessage(e.message);
        }
    }

    // api call of templateData end

    // api call of Suggest Data start
    const getSuggestList = (func) => {
        if (func) {
            setChildGetFunc(() => func); // Store the function once received from child
        } else if (childGetFunc) {
            childGetFunc(); // Call when postSuggest succeeds
        }
    };

    const postSuggest = () => {
        try {
            const { url, postKey, postKey2 } = props?.isNewLayout ? urlConfig[fieldName] || {} : urlConfig[props?.field_name] || {};

            if (!url || !props?.isSelected) return;
            const selection = window.getSelection();
            const selectedText = selection?.toString()?.trim();

            if (!selectedText) {
                props.errorMessage("Please select text to save as suggestion");
                return;
            }
            else if (selectedText.length > 100) {
                props.errorMessage("Please select text below 100 characters");
                return;
            }
            const data = {};
            if (props?.fromIp) {
                data['clinic_id'] = props?.clinicId || null
                data['doctor_id'] = props?.doctor_id || null
                data['ip_admission_id'] = props?.admission_id || null
                if (props.isNewLayout) {
                    data["system_name"] = props.systemName || "";
                    data["custom_name"] = Title;
                    data["comments"] = selectedText;
                } else {
                    data[postKey] = selectedText;
                }
            } else {
                if (props.isNewLayout) {
                    data["system_name"] = props.systemName || "";
                    data["custom_name"] = Title;
                    data["comments"] = selectedText;
                } else if (props.field_name === "allergies" && focusedEditor === "comments2") {
                    data[postKey2] = selectedText;
                } else {
                    data[postKey] = selectedText;
                }
            }

            RestAPIService.create(data, Serviceurls[url + "_SUGG_GET"])
                .then((response) => {
                    if (response?.data?.status === 'success') {
                        props.successMessage(response.data.message)
                        getSuggestList();
                    }
                }).catch((error) => {
                    props.errorMessage(error?.response?.data?.message || error.message)
                })
        } catch (error) {
            props.errorMessage(error.message)
        }
    }
    // api call of Suggest Data end

    const updateState = (updates) => {
        setState((prev) => ({ ...prev, ...updates }));
    };

    const TemplateActionButton = ({ label, onClick }) => (
        <Button
            color="primary"
            fullWidth
            sx={{ justifyContent: 'flex-start', pl: 2, textTransform: 'capitalize' }}
            onMouseDown={(e) => {
                e.preventDefault();
                onClick();
            }}
        >
            {label}
        </Button>
    );

    const renderSrchTemp = () => {
        return (<Autocomplete
            clearIcon
            size="small"
            disabled={ props.isLocked}
            sx={{ width: '16vw' }}
            options={state.templateList}
            value={state.selectedTemplateName}
            getOptionLabel={(option) => typeof option === 'string' ? option : option?.template_name}
            onChange={(e, value) => {
                updateState({
                    selectedTemplateName: value,
                    comments: props.isNewLayout ? value.comments ? (state.comments || '') + value.comments
                        : state.comments
                        : ((state.post_key && value[state.post_key])
                            ? (state.comments || '') + value[state.post_key]
                            : state.comments),
                    comments2: (state.post_key2 && value[state.post_key2])
                        ? (state.comments2 || '') + value[state.post_key2]
                        : state.comments2,
                });
                updateState({ selectedTemplateName: null })
            }}
            renderOption={(props, option) => (
                <li {...props} style={{ padding: '0vh 1.5vh' }}>
                    <ListItem sx={{ display: 'flex', justifyContent: 'space-between', p: 0, m: 0 }}>
                        <ListItemText
                            primary={
                                option?.template_name?.length > 30 ? (
                                    <Tooltip title={option.template_name} placement="top">
                                        <div>{option.template_name.slice(0, 21) + '...'}</div>
                                    </Tooltip>
                                ) : (
                                    option.template_name
                                )
                            }
                        />
                        <Tooltip title="Delete Template" placement="top" arrow>
                            <IconButton
                                edge="end"
                                color="error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    updateState({
                                        tempType: 'delete',
                                        deleteTemplateName: option,
                                        deletePopup: true,
                                    })
                                }}
                            >
                                <DeleteIcon sx={{ height: '2.5vh', width: '2.5vw' }} />
                            </IconButton>
                        </Tooltip>
                    </ListItem>
                </li>
            )}
            renderInput={(params) => <TextField {...params} label="Select Template Name" />}
            PaperComponent={({ children }) => (
                <Paper sx={{ maxHeight: '25vw', overflow: 'auto' }}>
                    {children}
                    <Divider />
                    <TemplateActionButton
                        label="Save As Template"
                        onClick={() => updateState({ openPopupAddNew: true, templateLoad: false, tempType: 'Add' })}
                    />
                    {state.selectedTemplateName && (
                        <TemplateActionButton
                            label="Edit Template"
                            onClick={() =>
                                updateState({
                                    openPopupAddNew: true,
                                    templateLoad: false,
                                    tempType: 'rename',
                                    templateName: state.selectedTemplateName.template_name,
                                })
                            }
                        />
                    )}
                </Paper>
            )}
        />)
    }

    const sharedProps = {
        Title: showTitle,
        field_name: props.field_name,
        systemName: props.systemName,
        isNewLayout: props.isNewLayout,
        EditorDisable: props.fromIp ? false : (props.isLocked),
        minHeight: "14vh",
        EditorHeight: "20vh",
        overallHeight: "auto",
        saveData: postToolsFieldData.bind(this),
        url: props?.isNewLayout ? urlConfig[fieldName]?.url + "_SUGG_GET" || {} : urlConfig[props?.field_name]?.url + "_SUGG_GET" || "",
        successMessage: props.successMessage,
        errorMessage: props.errorMessage,
        postKey: urlConfig[props?.field_name]?.postKey || "",
        postKey2: urlConfig[props?.field_name]?.postKey2 || "",
        isSelected: props?.isSelected,
        isSaveClicked: loader,
        isFromTemplate: Boolean(state?.selectedTemplateName?.template_name),
        fromIp: props.fromIp || false,
        clinicId:props?.clinicId,
        admission_id:props?.admission_id,
        doctor_id:props?.doctor_id,
        isClearClicked:clear,
        maxChar: props.isNewLayout ? 7000 : 600,
    };

    // render content

    return (
        <ShowComponents onIntersection={getApiCall.bind(this)}>
            <Card className='discharge_Sum_inner_container'
                sx={{
                    height: "fit-content",
                    background: "#FFFF",
                    marginBottom: "0.5vw",
                    border: `2px solid ${props.isSelected ? Colors.themeDark : Colors.white}`
                }}>
                <Box width="100%" display="flex" justifyContent={"space-between"} alignItems={"center"} marginTop={"0.3vw"}>
                    {renderTitle(showTitle, props.isSelected, !props?.fromIp && (props.field_name === "final_diagnosis" || props.field_name === "diagnosis") ? <Button
                        className='Common_Btn_Min_Width'
                        disabled={loader || props.isLocked}
                        onClick={() => { updateState({ PreviousDiagnosisPopup: true }) }}
                    >
                        <Tooltip placement='top' title={`Previous ${Title}`}>
                            <HistoryIcon color={(loader || props.isLocked) ? "disabled" : "primary"} sx={{ height: '1.7vw', width: '1.7vw' }} />
                        </Tooltip>
                    </Button> : null)}
                    <Box display="flex" alignItems={"center"} gap={"0.625dvw"}>
                        {renderSrchTemp()}
                        <ActionButton
                            text={"Clear"}
                            emed_tid='doc_notes_bold_clr'
                            variant={'outlined'}
                            onClick={() => {
                                if(state.comments === '' || state.comments === ''){
                                    setClear(true)
                                    setTimeout(() => setClear(false),50)
                                }else{
                                    updateState({ comments: "", comments2: "" })
                                }
                            }}
                        />
                        <ActionButton
                            text={"Save"}
                            emed_tid='doc_notes_bold_save'
                            disabled={loader || props.isLocked}
                            onClick={() => {setLoader(true);}}
                        />
                    </Box>
                </Box>
                {
                    props.field_name === "allergies" ?
                        <Box width={"100%"} display={"flex"} alignItems={"center"} marginTop={"0.5vw"}>
                            <Box width="50%" >
                                <Typography fontSize={'0.9vw'} fontWeight={600} color={Colors.SecondaryText}>Medication Allergy</Typography>
                                <SarasToolsFieldWithMic
                                    {...sharedProps}
                                    EditorRef={state.comments || ""}
                                    stateName="comments"
                                    onFocusChange={setFocusedEditor}
                                    callSuggestGet={getSuggestList}
                                />
                            </Box>
                            <Box width="50%">
                                <Typography fontSize={'0.9vw'} fontWeight={600} color={Colors.SecondaryText}>Non-Medication Allergy</Typography>
                                <SarasToolsFieldWithMic
                                    {...sharedProps}
                                    EditorRef={state.comments2 || ""}
                                    stateName="comments2"
                                    onFocusChange={setFocusedEditor}
                                    callSuggestGet={getSuggestList}
                                />
                            </Box>
                        </Box>
                        :
                        <SarasToolsFieldWithMic
                            {...sharedProps}
                            EditorRef={state.comments || ""}
                            stateName="comments"
                            onFocusChange={setFocusedEditor}
                            callSuggestGet={getSuggestList}
                        />
                }
                {state.openPopupAddNew && <TemplatePopup
                    open={state.openPopupAddNew}
                    Title={Title}
                    onClose={() => updateState({ openPopupAddNew: false, templateLoad: false, templateName: "" })}
                    templateName={state.templateName || ''}
                    setTemplateName={(value) => updateState({ templateName: value })}
                    tempType={state.tempType}
                    onSubmit={postNewTemplateData}
                    loading={state.templateLoad}
                />}
                {state.deletePopup && <DeletePopup
                    DeletTitle={`Are you sure you want to delete ${state.deleteTemplateName?.template_name} ?`}
                    deleteAlertPopupClose={() => { updateState({ deletePopup: false, isDeleteCliked: false }) }}
                    removeData={deleteTemplateData.bind(this)}
                    disable={state.removeData}
                />}
                {state.PreviousDiagnosisPopup ?
                    <PreviousDiagnosis
                        key={props.field_name}
                        title={props.field_name === "final_diagnosis" ? Title : "Provisional Diagnosis"}
                        title2={props.field_name === "final_diagnosis" ? Title : "Provisional Diagnosis"}
                        dateList={props.field_name === "final_diagnosis" ? state.fnlDateList : state.dateList}
                        isIcdCode={false}
                        is_loading = {state.is_previous_loading}
                        previousData={props.field_name === "final_diagnosis" ? state.fnlDiagnosisData || [] : state.diagnosisData || []}
                        onDateChange={(date) => { getPreviousDiagnosis(date) }}
                        closePopup={() => { updateState({ PreviousDiagnosisPopup: false }) }}
                        onCopyDiagnosis={(data) => { postToolsFieldData("comments", data?.diagnosis || "") }}
                    />
                    : null}
            </Card>
        </ShowComponents>
    );
};

export default SarasBoldItalic;